<script setup lang="ts">
import { computed, getCurrentInstance, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { debounce } from 'lodash-es';
import { ModalRef } from '@/types/elements/modal';
import useDevice from '@/hooks/useDevice';
import { formAutoCompleteFns, FormAutoCompleteProps } from '@/types/form/form-auto-complete';
import { FormDropdownOption } from '@/types/form/form-dropdown/form-dropdown';
import { FormElMsgData } from '@/types/form/others/form-el-msg';
import FormElMsg from '@/form/other/FormElMsg.vue';
import utils from '@/utils';
import { SitecoreContext } from '@/types/layout';
import { FormInputRef } from '@/types/form/form-input';
import { DynamicFormContext } from '@/types/form/dynamic-form';
import FormAutoCompleteOptions from '@/form/FormAutoComplete/FormAutoCompleteOptions.vue';

const { isMobileOrTablet } = useDevice();
const app = getCurrentInstance();
const $jss = app?.appContext.config.globalProperties.$jss;
const sitecoreContext = $jss?.sitecoreContext() as SitecoreContext;
const lang = sitecoreContext?.language;
const formContext = inject<DynamicFormContext>('formContext')!;
const emit = defineEmits(['search', 'change']);
const props = defineProps<FormAutoCompleteProps>();
const rootEl = ref();
const inputEl = ref<HTMLInputElement>();
const modalSearchEl = ref();
const value = ref('');
const options = ref<FormDropdownOption[]>([]);
const loading = ref(true);
const focused = ref(false);
const optionsModalRef = ref<ModalRef>();
const isRequired = ref(props.item?.fields?.required?.value ?? false);
const isDisabled = ref(false);
const isVisible = ref(true);
const msg = ref<FormElMsgData>();
const fields = computed(() => props.item?.fields);
const hasValue = computed(() => !utils.obj.isNoE(value.value));
const label = computed(() => fields.value?.label?.value || '');
const placeholder = computed(() => fields.value?.placeholder?.value || '');
const fieldName = computed(() => fields.value?.fieldName?.value || '');
const search = async (val: string) => {
  const type = utils.sp.getVal(fields.value?.type);
  const alpha2Code = utils.i18n.getAlpha2Code(lang);
  if (!type) return;
  loading.value = true;
  options.value = await formAutoCompleteFns[type]({
    country: alpha2Code,
    keywords: val,
    limit: 8,
  });
  loading.value = false;
};
const onInput = debounce(async (e) => {
  if (isMobileOrTablet.value) return;
  const val = e.target.value.trim();
  msg.value = undefined;
  await search(val);
}, 600);
const onPopInput = debounce(async (e) => {
  const val = e.target.value.trim();
  msg.value = undefined;
  await search(val);
}, 600);
const onFocus = async (e: any) => {
  focused.value = true;
  if (isMobileOrTablet.value) {
    await optionsModalRef.value?.open();
    modalSearchEl.value?.focus();
  }
};
const onMainClick = () => {
  inputEl.value?.focus();
};
const onOptionClick = (option: FormDropdownOption) => {
  focused.value = false;
  value.value = option.value;
  emit('change', 'form-auto-complete', fieldName.value, option.value, option);
  if (isMobileOrTablet.value) {
    optionsModalRef.value?.close();
  }
};
const onSearchIconClick = async (e: any) => {
  focused.value = true;
  if (isMobileOrTablet.value) {
    await optionsModalRef.value?.open();
    modalSearchEl.value?.focus();
  } else {
    e.currentTarget.previousSibling.focus();
  }
};
const onBodyClick = (e: any) => {
  if (isMobileOrTablet.value) return;
  if (
    !rootEl.value.contains(e.target) &&
    rootEl.value !== e.target &&
    !optionsModalRef.value?.el.value?.contains(e.target) &&
    optionsModalRef.value?.el.value !== e.target
  ) {
    focused.value = false;
  }
};
const setRequired = (required: boolean) => {
  isRequired.value = required;
};

const setDisabled = (disabled: boolean) => {
  isDisabled.value = disabled;
};

const setVisible = (visible: boolean) => {
  isVisible.value = visible;
};

const updateValue = (newValue?: string | null) => {
  value.value = newValue || '';
};

const getValue = (newValue?: string | null) => {
  return value.value;
};

const validate = async () => {
  const result = {
    value: value.value,
    valid: true,
    message: '',
  };

  if (!isVisible.value) {
    return result;
  }

  if (isRequired.value && utils.obj.isNoE(value.value)) {
    result.valid = false;
    result.message = fields.value?.requiredMsg?.value || 'This field is required';
    msg.value = {
      type: 'error',
      message: result.message,
    };
    return result;
  }
  return result;
};
watch(
  () => [isMobileOrTablet, focused],
  ([_mt, _f]) => {
    if (_mt.value && _f.value) {
      optionsModalRef.value?.open();
    } else {
      optionsModalRef.value?.close();
    }
  }
);
const getRefData = (): FormInputRef => ({
  el: rootEl.value!,
  type: 'form-auto-complete',
  name: fieldName.value,
  id: props.item?.id ?? '',
  visible: isVisible.value,
  setRequired,
  setDisabled,
  setVisible,
  updateValue,
  getValue,
  validate,
});
watch(
  () => props.item,
  () => {
    formContext.regFormEl(getRefData());
  },
  { immediate: true }
);
onMounted(() => {
  formContext.regFormEl(getRefData());
  document.body.addEventListener('click', onBodyClick);
});
onBeforeUnmount(() => {
  document.body.removeEventListener('click', onBodyClick);
  formContext.unRegFormEl(getRefData());
});

defineExpose(getRefData());
</script>

<template>
  <div class="f-autocomplete" v-if="fields" ref="rootEl">
    <div class="f-autocomplete__content" :class="[{ active: focused }]">
      <div class="f-autocomplete__main" @click="onMainClick">
        <div class="f-autocomplete__placeholder" :class="[{ 'as-label': focused || hasValue }]">
          <span
            class="f-autocomplete__label"
            v-html="focused || hasValue ? label : utils.string.ifEmpty(placeholder, label)"
          />
          <span class="f-autocomplete__required" v-if="isRequired">*</span>
        </div>
        <input type="text" @input="onInput" @focus="onFocus" v-model="value" ref="inputEl" />
      </div>
      <transition name="options-ani">
        <div class="f-autocomplete__pop-up" v-if="!isMobileOrTablet && value.length > 1 && focused">
          <FormAutoCompleteOptions
            :loading="loading"
            :options="options"
            :value="value"
            @option-click="onOptionClick"
          />
        </div>
      </transition>
    </div>
    <form-el-msg :msg="msg" />
    <modal
      class="f-autocomplete__modal"
      ref="optionsModalRef"
      animation="bottom-slide-in"
      :fire-gtm="false"
      closable
      :sticky="false"
    >
      <div class="f-autocomplete__modal-search">
        <div class="f-autocomplete__modal-input">
          <icon name="search" size="xxs" @click="onSearchIconClick" />
          <input
            type="text"
            :placeholder="fields.placeholder.value"
            @input="onPopInput"
            v-model="value"
            ref="modalSearchEl"
          />
        </div>
      </div>
      <FormAutoCompleteOptions
        :loading="loading && value.length > 1"
        :options="options"
        :value="value"
        @option-click="onOptionClick"
      />
    </modal>
  </div>
</template>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';
.f-autocomplete {
  $c: &;
  $m: '.e-modal';
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: var(--item-color, $color-black);
  &__content {
    position: relative;
    &.active {
      z-index: 3;
    }
  }
  &__title {
    margin-bottom: 24px;
    font-size: 26px;
    font-weight: 500;
    &:empty {
      display: none;
    }
  }
  &__placeholder {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    pointer-events: none;
    display: flex;
    align-items: center;
    gap: 4px;
    color: var(--item-color, #929292);
    font-size: 16px;
    padding: 10px 16px;
    transition: all 0.2s ease-in-out;
    &.as-label {
      font-size: 12px;
      height: 36px;
      opacity: 0.7;
    }
  }
  &__main {
    position: relative;
    height: 60px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border-radius: 4px;
    border: 1px solid var(--item-border-color, #c4c4c4);
    background: var(--item-back-color, #ffffff);
    input {
      align-self: flex-end;
      border: none !important;
      outline-width: 0 !important;
      padding: 0 !important;
      margin-bottom: 0 !important;
      flex-grow: 1;
      &::placeholder {
        color: $grey;
        @include h9;
      }
    }
  }
  &__messages {
    height: 40px;
    line-height: 1.3;
    font-size: 12px;
    padding-top: 8px;
    text-align: end;
  }
  &__error-msg {
    color: $red;
  }
  &.has-error {
    #{$c}__main {
      border-color: $red;
    }
  }
  &__pop-up {
    position: absolute;
    top: 64px;
    width: 100%;
    border: 1px solid var(--item-border-color, #c4c4c4);
    background: var(--item-curtain-back-color, #ffffff);
    color: var(--item-curtain-color, #000);
    box-shadow: 0 5px 10px rgba(0, 0, 0, 0.4);
    border-radius: 4px;
  }
  &__modal {
    align-items: flex-end;
    &.e-modal {
      #{$m}__content {
        width: 100%;
        padding: 48px 0 24px 0;
      }
    }
    &-search {
      background: $grey-light;
      padding: 16px;
    }
    &-input {
      height: 32px;
      border: 1px solid transparent;
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0 16px;
      gap: 8px;
      background: $white;
      border-radius: 16px;
      input {
        border: none !important;
        outline-width: 0 !important;
        padding: 0 !important;
        margin-bottom: 0 !important;
        flex-grow: 1;
        &::placeholder {
          color: $grey;
          @include h9;
        }
      }
      .e-icon {
        color: $grey;
        svg {
          width: 16px;
          height: auto;
        }
      }
    }
  }
}
</style>
